import React, { FC, Fragment, useState, useContext } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { ProductionValueTemplate } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useValueTemplate } from 'hooks/useValueTemplate';
import AddEditValueTemplateDialog from './add-edit-value-template-dialog/add-edit-value-template-dialog.component';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 100 },
    { type: 'text', width: 100 },
    { type: 'button', width: 80 },
];

const ProductionValueTemplates: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const {
        valueTemplates: rawValueTemplates,
        valueTemplatesLoading,
        deleteValueTemplate,
        deleteTemplateLoading,
    } = useValueTemplate();
    const valueTemplates: ProductionValueTemplate[] = rawValueTemplates;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<ProductionValueTemplate | null>(null);

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedTemplate(null);
    };

    const openDialog = (template: ProductionValueTemplate | null = null) => {
        setSelectedTemplate(template);
        setDialogOpen(true);
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Button startIcon={<AddIcon />} onClick={() => openDialog()}>
                    {translate('lab-settings:ADD_PRODUCTION_VALUE_TEMPLATE')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('lab-settings:PRODUCTION_VALUE_TEMPLATE')}</TableCell>
                                <TableCell>{translate('lab-settings:PRODUCTION_VALUE_STEPS')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {valueTemplatesLoading && (
                                <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />
                            )}
                            {valueTemplates.length > 0 &&
                                valueTemplates.map((template, index) => (
                                    <TableRow
                                        key={index}
                                        hover
                                        onClick={() => openDialog(template)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{template.name}</TableCell>
                                        <TableCell>{template.steps.map((step) => step.name).join(', ')}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title={translate('common:COPY')}>
                                                <IconButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDialog({
                                                            ...template,
                                                            _id: undefined,
                                                            name: `${translate('lab-settings:COPY_OF')} ${template.name}`,
                                                        });
                                                    }}
                                                >
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={translate('common:DELETE')}>
                                                <IconButton
                                                    disabled={deleteTemplateLoading}
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        const result = await deleteValueTemplate({
                                                            templateId: template._id || '',
                                                        });
                                                        if (result) {
                                                            setAppToasterContent({
                                                                isOpen: true,
                                                                severity: 'success',
                                                                message: translate(
                                                                    'lab-settings:PRODUCTION_VALUE_TEMPLATE_DELETED_SUCCESSFULLY',
                                                                ),
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {valueTemplates.length === 0 && !valueTemplatesLoading && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        {translate('lab-settings:NO_PRODUCTION_VALUE_TEMPLATES_FOUND')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <AddEditValueTemplateDialog
                productionTemplate={selectedTemplate ?? undefined}
                open={dialogOpen}
                onDialogClose={handleDialogClose}
            />
        </Fragment>
    );
};

export default ProductionValueTemplates;
